/**
 * Cliente tipado do fluxo de checkout do evento mensal.
 *
 * Antes, CheckoutFormViewModel.js lia a resposta de /checkout-publico por
 * sondagem de campo (`data.alreadyRegistered`, `data.pendingApproval`,
 * `data.pixQrCode`, etc.) sem nenhuma garantia de que o campo existia. A
 * resposta real é polimórfica por desenho (varia por plano/método/estado
 * existente — ver CheckoutPublicoResponse em specs/openapi.yaml), então os
 * tipos abaixo mantêm os campos opcionais, mas pelo menos nomeados e
 * verificados contra o spec em vez de inventados no consumidor.
 */
import { api } from '@/services/api';
import type { operations } from '@/types/api';

type CheckoutDetalhesEnvelope =
  operations['obterDetalhesCheckout']['responses']['200']['content']['application/json'];

export type CheckoutDetalhes = NonNullable<
  CheckoutDetalhesEnvelope['detalhes']
>;

export type SituacaoCpf =
  operations['verificarCadastroCpf']['responses']['200']['content']['application/json']['situacao'];

export type CheckoutPublicoRequest =
  operations['criarCheckoutPublico']['requestBody']['content']['application/json'];

export type CheckoutPublicoResponse =
  operations['criarCheckoutPublico']['responses']['200']['content']['application/json'];

export type StatusPagamentoResponse =
  operations['verificarStatusPagamento']['responses']['200']['content']['application/json'];

export type UploadComprovanteResponse =
  operations['uploadComprovante']['responses']['201']['content']['application/json'];

export type PagamentoPorTokenInfo =
  operations['obterPagamentoPorToken']['responses']['200']['content']['application/json'];

export type ProcessarPagamentoPorTokenRequest = NonNullable<
  operations['processarPagamentoPorToken']['requestBody']
>['content']['application/json'];

export type ProcessarPagamentoPorTokenResponse =
  operations['processarPagamentoPorToken']['responses']['200']['content']['application/json'];

// ── Evento especial (sistema paralelo, ver memoria eventos_especiais) ──

export type SpecialEventPublico =
  operations['obterEventoEspecialPorSlug']['responses']['200']['content']['application/json'];

export type CriarCompraEventoEspecialRequest =
  operations['criarCompraEventoEspecial']['requestBody']['content']['application/json'];

export type CriarCompraEventoEspecialResponse =
  operations['criarCompraEventoEspecial']['responses']['200']['content']['application/json'];

export type StatusCompraEventoEspecialResponse =
  operations['verificarStatusCompraEventoEspecial']['responses']['200']['content']['application/json'];

export async function fetchCheckoutDetalhes(
  slug: string,
): Promise<CheckoutDetalhes> {
  const { data } = await api.get<CheckoutDetalhesEnvelope>(
    '/eventos/checkout-detalhes',
    { params: { slug } },
  );
  return data.detalhes as CheckoutDetalhes;
}

export async function verificarCadastroCpf(cpf: string): Promise<SituacaoCpf> {
  const cpfLimpo = cpf.replace(/\D/g, '');
  const { data } = await api.get<{ situacao: SituacaoCpf }>(
    `/eventos/verificar-cadastro/${cpfLimpo}`,
  );
  return data.situacao;
}

export async function criarCheckoutPublico(
  payload: CheckoutPublicoRequest,
): Promise<CheckoutPublicoResponse> {
  const { data } = await api.post<CheckoutPublicoResponse>(
    '/eventos/checkout-publico',
    payload,
  );
  return data;
}

export async function verificarStatusPagamento(
  registrationId: string,
): Promise<StatusPagamentoResponse> {
  const { data } = await api.get<StatusPagamentoResponse>(
    `/eventos/status-pagamento/${registrationId}`,
  );
  return data;
}

export async function uploadComprovante(
  arquivo: File,
): Promise<UploadComprovanteResponse> {
  const form = new FormData();
  form.append('comprovante', arquivo);
  const { data } = await api.post<UploadComprovanteResponse>(
    '/eventos/upload-comprovante',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function obterPagamentoPorToken(
  token: string,
): Promise<PagamentoPorTokenInfo> {
  const { data } = await api.get<PagamentoPorTokenInfo>(
    `/eventos/pagar/${token}`,
  );
  return data;
}

export async function processarPagamentoPorToken(
  token: string,
  payload: ProcessarPagamentoPorTokenRequest,
): Promise<ProcessarPagamentoPorTokenResponse> {
  const { data } = await api.post<ProcessarPagamentoPorTokenResponse>(
    `/eventos/pagar/${token}`,
    payload,
  );
  return data;
}

export async function obterEventoEspecialPorSlug(
  slug: string,
): Promise<SpecialEventPublico> {
  const { data } = await api.get<SpecialEventPublico>(
    `/eventos-especiais/${slug}`,
  );
  return data;
}

export async function criarCompraEventoEspecial(
  payload: CriarCompraEventoEspecialRequest,
): Promise<CriarCompraEventoEspecialResponse> {
  const { data } = await api.post<CriarCompraEventoEspecialResponse>(
    '/eventos-especiais/checkout',
    payload,
  );
  return data;
}

export async function verificarStatusCompraEventoEspecial(
  purchaseId: string,
): Promise<StatusCompraEventoEspecialResponse> {
  const { data } = await api.get<StatusCompraEventoEspecialResponse>(
    `/eventos-especiais/status/${purchaseId}`,
  );
  return data;
}

/* ── Cupons ─────────────────────────────────────────────────────────
   O valor base é calculado no servidor a partir do evento; o cliente só
   envia o código e onde ele está sendo usado. */
export interface CupomValidacao {
  valido: boolean;
  motivo?: string;
  codigo?: string;
  descontoCentavos?: number;
  valorFinalCentavos?: number;
}

export async function validarCupomEvento(payload: {
  codigo: string;
  slug: string;
  plano: string;
}): Promise<CupomValidacao> {
  const { data } = await api.post<CupomValidacao>(
    '/eventos/cupons/validar',
    payload,
  );
  return data;
}

export async function validarCupomEventoEspecial(payload: {
  codigo: string;
  slug: string;
}): Promise<CupomValidacao> {
  const { data } = await api.post<CupomValidacao>(
    '/eventos-especiais/cupons/validar',
    payload,
  );
  return data;
}
